import { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from 'sweetalert2'; 
import * as yup from "yup";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, CircularProgress, MenuItem,
    Grid, TextField, IconButton, Typography, FormControl,InputLabel
} from "@mui/material";
import MDButton from "components/MDButton";

import { Add, Remove } from "@mui/icons-material";
import api from "core/interceptors/api";


// ------------------------------------------------------------
// Yup scheme
// ------------------------------------------------------------

const validationServiceSpecializationSchema = yup.object().shape({
    service_type: yup
      .string()
      .required("Service type is required"),
    
    service_specializations: yup
      .array()
      .of(
        yup
          .string()
          .required("Specialization is required")
          .min(3, "Minimum 3 characters required")
          .max(50, "Maximum 50 characters allowed")
          .matches(
            /^[A-Za-z ]+$/,
            "Only letters and spaces allowed (no numbers or special characters)"
          )
      )
      .min(1, "At least one specialization is required")
  });
  

// ------------------------------------------------------------

export default function ServiceSpecializationEditModel({ open, handleClose, editInput }) {
    console.log(editInput);
    
    useEffect(() => {
        fetchServiceTypes();
    }, []);

    // ----------------------------------------------------------
    // Service Specializations Add Model State Variables And Handles
    // ----------------------------------------------------------

    const [loading, setLoading] = useState(true);
    const [serviceTypes, setServiceTypes] = useState([]);

    const fetchServiceTypes = async () => {
        setLoading(true);
        try {
            const response = await api.get("/service_management/service_types/");
            console.log("Fetched products:", response);
            const fetchedServiceTypes = response.data.service_types

            setServiceTypes(fetchedServiceTypes);

        } catch (error) {
            console.error("Error fetching service types:", error);
            Swal.fire("Error", "Failed to load service types. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };

    // ------------------------------------------------------------
    // Formik
    // ------------------------------------------------------------

    const initialValues = { 
        ...editInput, 
        service_specialization_id: editInput.service_specialization_id || '',
        service_type: editInput.service_type || '', 
        service_specializations: editInput.specialization || [""] 
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationServiceSpecializationSchema,
        onSubmit: async (values) => {
            try {
                console.log("Form Submitted:", values);

                const response = await api.put(`/service_management/service_specializations/${values.service_specialization_id}`, values, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                handleClose();

                if (response.data.status === 1) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Service Type Specializations Updated Successfully!',
                        timer: 3000,
                        timerProgressBar: true,
                        showConfirmButton: false,
                    });

                    formik.resetForm();
                } 
                
            } catch (error) {
                console.error('Error updating vendor:', error);
            } finally {
                formik.resetForm();
            }
        },
        enableReinitialize: true,
    });

    // ------------------------------------------------------------

       // ------------------------------------------------------------
    // Service Specializations Drop Down Handles
    // ------------------------------------------------------------

    const addSpecialization = () => {
        formik.setFieldValue("service_specializations", [...formik.values.service_specializations, ""]);
    };

    const removeSpecialization = (index) => {
        const newList = [...formik.values.service_specializations];
        newList.splice(index, 1);
        formik.setFieldValue("service_specializations", newList);
    };

    // ------------------------------------------------------------
    return (
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Edit Service Type</DialogTitle>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit(e);
                }}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            

                            {/* Service Type Dropdown */}
                            <Grid item xs={11}
                                display="flex"
                                justifyContent="center"
                                alignItems="center">

                                <FormControl fullWidth margin="normal">

                                    <InputLabel id="service-type-label">Service Type</InputLabel>
                                    <Select
                                        labelId="service-type-label"
                                        label="Service Type"
                                        name="service_type"
                                        value={formik.values.service_type}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        sx={{ height: '45px' }}
                                        error={formik.touched.service_type && Boolean(formik.errors.service_type)}
                                    >

                                        {
                                            (loading && serviceTypes.length === 0)  ?
                                                <Grid item xs={12}
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center">
                                                    <CircularProgress color="primary" />
                                                </Grid> :
                                                serviceTypes.map((serviceType) => (
                                                    <MenuItem key={serviceType.id} value={serviceType.service_type}>
                                                        {serviceType.service_type}
                                                    </MenuItem>
                                                ))
                                        }
                                    </Select>
                                    {formik.touched.service_type && formik.errors.service_type && (
                                        <Typography color="error" variant="body2">{formik.errors.service_type}</Typography>
                                    )}
                                </FormControl>
                            </Grid>

                             {/* Service Specializations Dynamic Input Fields */}
                        {formik.values.service_specializations.map((value, index) => (
                            <Grid key={index} item xs={12} display="flex" alignItems="center">
                                <TextField
                                    fullWidth
                                    label={`Specialization ${index + 1}`}
                                    name={`service_specializations[${index}]`}
                                    value={value}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.service_specializations?.[index] && Boolean(formik.errors.service_specializations?.[index])}
                                    helperText={formik.touched.service_specializations?.[index] && formik.errors.service_specializations?.[index]}
                                />
                                <IconButton onClick={() => removeSpecialization(index)} color="error" disabled={formik.values.service_specializations.length === 1}>
                                    <Remove />
                                </IconButton>
                            </Grid>
                        ))}
                        </Grid>
                         {/* Add New Input Field Button */}
                         <Grid item xs={12} display="flex" justifyContent="center">
                            <IconButton onClick={addSpecialization} color="primary">
                                <Add />
                            </IconButton>
                        </Grid>
                    </DialogContent>
    
                    <DialogActions>
                        <Button onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        <MDButton type="submit" color="success">Save</MDButton>
                    </DialogActions>
                </form>
            </Dialog>
        )

}